import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { SupportedLanguage, SUPPORTED_LANGUAGES, changeLanguage } from '../i18n';
import { useAuth } from './AuthContext';
import userService from '../services/userService';
import AppLoadingSkeleton from '../components/skeletons/AppLoadingSkeleton';

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  isChangingLanguage: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Return default fallback values instead of throwing in production
    return {
      currentLanguage: 'en' as SupportedLanguage,
      changeLanguage: async () => {},
      isChangingLanguage: false
    };
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user, token, updateCurrentUser } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('en');
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Track i18n initialization state to prevent translation key flash
  const [isI18nReady, setIsI18nReady] = useState(i18n.isInitialized);

  // Use translation hook normally - React will handle hook order consistency
  const { i18n: i18nInstance } = useTranslation();

  // Wait for i18n to finish loading translations before rendering children
  useEffect(() => {
    // If already initialized, we're good
    if (i18n.isInitialized) {
      setIsI18nReady(true);
      return;
    }

    // Listen for initialization complete
    const handleInitialized = () => {
      setIsI18nReady(true);
    };

    // i18next emits 'initialized' when init() promise resolves
    i18n.on('initialized', handleInitialized);

    // Also listen for 'loaded' as a fallback (fires when resources are loaded)
    i18n.on('loaded', handleInitialized);

    return () => {
      i18n.off('initialized', handleInitialized);
      i18n.off('loaded', handleInitialized);
    };
  }, []);
  
  // Update current language from i18n if available
  useEffect(() => {
    if (i18nInstance?.language && SUPPORTED_LANGUAGES.includes(i18nInstance.language as SupportedLanguage)) {
      setCurrentLanguage(i18nInstance.language as SupportedLanguage);
      // Set initial <html lang> attribute to match detected language
      document.documentElement.lang = i18nInstance.language;
    }
  }, [i18nInstance?.language]);

  // Sync with i18next language changes and update <html lang> attribute
  useEffect(() => {
    if (!i18nInstance) return;

    const handleLanguageChange = (lng: string) => {
      if (SUPPORTED_LANGUAGES.includes(lng as SupportedLanguage)) {
        setCurrentLanguage(lng as SupportedLanguage);
        // Keep <html lang> in sync for accessibility and to prevent
        // the htmlTag detector from returning a stale value on reload
        document.documentElement.lang = lng;
      }
    };

    try {
      i18nInstance.on('languageChanged', handleLanguageChange);
      return () => {
        i18nInstance.off('languageChanged', handleLanguageChange);
      };
    } catch (error) {
      // Error handled silently
    }
  }, [i18nInstance]);

  // Initialize language from user preference when user logs in
  useEffect(() => {
    // DON'T override if user has manually changed language this session
    const userChangedLanguage = sessionStorage.getItem('userChangedLanguage');
    if (userChangedLanguage === 'true') {
      return; // User's choice takes precedence
    }

    if (user?.preferredLanguage && SUPPORTED_LANGUAGES.includes(user.preferredLanguage as SupportedLanguage)) {
      const userLanguage = user.preferredLanguage as SupportedLanguage;

      // Only sync if there's actually a mismatch and i18n is not already changing
      if (userLanguage !== currentLanguage && userLanguage !== i18nInstance?.language) {
        // Use i18n.changeLanguage directly to avoid the backend call in handleChangeLanguage
        // since the backend already has the correct language preference
        i18nInstance?.changeLanguage(userLanguage).then(() => {
          localStorage.setItem('i18nextLng', userLanguage);
        }).catch(() => {
          // Error handled silently
        });
      }
    }
  }, [user?.preferredLanguage]); // Removed currentLanguage from dependencies to prevent cascade

  /**
   * Change language and update backend preference
   * NOTE: This function makes an API call to update the backend.
   * If the backend has already been updated (e.g., from ProfileSettings),
   * use i18n.changeLanguage() directly instead to avoid duplicate API calls.
   */
  const handleChangeLanguage = useCallback(async (language: SupportedLanguage) => {
    if (language === currentLanguage) return;

    setIsChangingLanguage(true);
    try {
      // Change i18next language
      await changeLanguage(language);

      // Update user preference in backend if authenticated
      if (user && token) {
        try {
          const response = await userService.updateProfile(
            { preferredLanguage: language },
            token
          );
          // Extract user object from response (backend returns {user: {...}})
          const updatedUser = response.user || response;
          updateCurrentUser(updatedUser);
        } catch (error) {
          // Don't throw error - language change in UI should still work
        }
      }

      setCurrentLanguage(language);
    } catch (error) {
      throw error;
    } finally {
      setIsChangingLanguage(false);
    }
  }, [currentLanguage, user, token, updateCurrentUser]);

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage: handleChangeLanguage,
    isChangingLanguage
  };

  // Show loading skeleton until i18n translations are loaded
  // This prevents the flash of translation keys (e.g., "common:actions.save")
  if (!isI18nReady) {
    return <AppLoadingSkeleton />;
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};